import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { memberApi, checkApiHealth } from '@/lib/api';
import Navigation from '@/components/shared/Navigation';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CreditCard, User, History, Settings, Download, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import LoadingSpinner from '@/components/shared/LoadingSpinner';
import { toast } from '@/components/ui/use-toast';

// Debug function to test API connectivity
const testApiConnection = async () => {
  try {
    console.log('🔧 Testing API connection...');
    console.log('API Base URL:', 'http://127.0.0.1:8000/api');
    console.log('Auth Token:', localStorage.getItem('auth_token'));
    console.log('Stored User:', localStorage.getItem('user'));
    
    // Test a simple API call
    const response = await fetch('http://127.0.0.1:8000/api/user', {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true'
      }
    });
    
    console.log('API Response Status:', response.status);
    const data = await response.json();
    console.log('API Response Data:', data);
    
    return { success: response.ok, status: response.status, data };
  } catch (error) {
    console.error('API Connection Test Failed:', error);
    return { success: false, error };
  }
};

const Dashboard = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setIsLoading(true);
        console.log('🔄 Fetching dashboard data...');
        console.log('User data:', user);
        console.log('Auth token:', localStorage.getItem('auth_token'));
        
        const data = await memberApi.getDashboard();
        console.log('✅ Dashboard data received:', data);
        setDashboardData(data);
        setError(null);
      } catch (error: any) {
        console.error('❌ Error fetching dashboard data:', error);
        console.error('Error response:', error.response?.data);
        console.error('Error status:', error.response?.status);
        
        let errorMessage = 'Failed to load dashboard data';
        if (error.response?.status === 403) {
          errorMessage = 'Access denied. Please check your permissions.';
        } else if (error.response?.status === 401) {
          errorMessage = 'Authentication required. Please login again.';
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        }
        
        setError(errorMessage);
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      fetchDashboardData();
    } else {
      setIsLoading(false);
    }
  }, [user]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="flex items-center justify-center min-h-[50vh]">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">
              Welcome back, {user?.first_name || user?.name || 'Member'}! 🏀
            </h1>
            <p className="text-gray-600 mt-2">
              Manage your membership and access your digital cards
            </p>
          </div>
          
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-6">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-red-600 mb-2">Unable to Load Dashboard Data</h3>
                <p className="text-red-600 mb-4">{error}</p>
                <div className="space-x-4">
                  <Button 
                    onClick={() => window.location.reload()} 
                    variant="outline"
                    className="border-red-300 text-red-600 hover:bg-red-100"
                  >
                    Retry
                  </Button>
                  <Button 
                    onClick={async () => {
                      const result = await checkApiHealth();
                      console.log('API Health Check Result:', result);
                      toast({
                        title: result.success ? "API Connected" : "API Error",
                        description: result.success ? `Status: ${result.status}` : `Error: ${result.error}`,
                        variant: result.success ? "default" : "destructive",
                      });
                    }} 
                    variant="outline"
                    className="border-blue-300 text-blue-600 hover:bg-blue-100"
                  >
                    Test API
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Create stats from real data or fallback to defaults
  const stats = [
    { 
      title: 'Membership Status', 
      value: dashboardData?.member?.status || 'Active', 
      icon: User, 
      color: 'text-green-600' 
    },
    { 
      title: 'Next Payment', 
      value: dashboardData?.next_payment_due || 'Jul 01, 2025', 
      icon: CreditCard, 
      color: 'text-blue-600' 
    },
    { 
      title: 'Jersey Number', 
      value: dashboardData?.member?.jersey_number ? `#${dashboardData.member.jersey_number}` : '#23', 
      icon: User, 
      color: 'text-basketball-orange-500' 
    },
    { 
      title: 'Member Since', 
      value: dashboardData?.member?.created_at 
        ? new Date(dashboardData.member.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
        : 'Jan 2025', 
      icon: History, 
      color: 'text-purple-600' 
    },
  ];

  const handleDownloadCard = async () => {
    try {
      const blob = await memberApi.downloadMyDigitalCard();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `digital-card-${user?.first_name || 'member'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Success",
        description: "Digital card downloaded successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to download digital card",
        variant: "destructive",
      });
    }
  };

  const handleEmailCard = async () => {
    try {
      if (!user?.email) {
        toast({
          title: "Error", 
          description: "Email address not found",
          variant: "destructive",
        });
        return;
      }
      
      await memberApi.emailMyDigitalCard(user.email, "Here is your digital membership card.");
      toast({
        title: "Success",
        description: "Digital card sent to your email",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to email digital card",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Welcome back, {user?.first_name || user?.name || 'Member'}! 🏀
          </h1>
          <p className="text-gray-600 mt-2">
            Manage your membership and access your digital cards
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, index) => (
            <Card key={index}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                    <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
                  </div>
                  <stat.icon className={`h-8 w-8 ${stat.color}`} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Digital Cards */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <CreditCard className="h-5 w-5" />
                  <span>My Digital Cards</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-basketball-orange-500 to-basketball-blue-500 rounded-lg p-6 text-white">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-lg font-semibold">Standard Card</h3>
                        <p className="text-sm opacity-90">
                          {dashboardData?.digital_card?.card_number || 'OGB2025184917'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm opacity-90">Expires</p>
                        <p className="font-semibold">
                          {dashboardData?.digital_card?.expiry_date 
                            ? new Date(dashboardData.digital_card.expiry_date).toLocaleDateString('en-US', { month: '2-digit', year: '2-digit' })
                            : '06/26'
                          }
                        </p>
                      </div>
                    </div>
                    <div className="flex justify-between items-end">
                      <div>
                        <p className="text-sm opacity-90">Member</p>
                        <p className="font-semibold">
                          {user?.first_name && user?.last_name 
                            ? `${user.first_name} ${user.last_name}`
                            : user?.name || 'Member'
                          }
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm opacity-90">Status</p>
                        <p className="font-semibold">
                          {dashboardData?.member?.membership_type || 'Active OG'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex space-x-3">
                    <Link to="/my-card" className="flex-1">
                      <Button className="w-full bg-basketball-gradient hover:opacity-90">
                        View Premium Card
                      </Button>
                    </Link>
                    <Button variant="outline" size="icon" onClick={handleDownloadCard}>
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="icon" onClick={handleEmailCard}>
                      <Mail className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Link to="/profile">
                  <Button variant="outline" className="w-full justify-start">
                    <User className="h-4 w-4 mr-2" />
                    Edit Profile
                  </Button>
                </Link>
                <Link to="/payments">
                  <Button variant="outline" className="w-full justify-start">
                    <History className="h-4 w-4 mr-2" />
                    Payment History
                  </Button>
                </Link>
                <Link to="/settings">
                  <Button variant="outline" className="w-full justify-start">
                    <Settings className="h-4 w-4 mr-2" />
                    Settings
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Recent Activity</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dashboardData?.recent_activity?.length > 0 ? (
                    dashboardData.recent_activity.slice(0, 3).map((activity: any, index: number) => (
                      <div key={index} className="flex items-center space-x-3 text-sm">
                        <div className={`w-2 h-2 rounded-full ${
                          activity.type === 'payment' ? 'bg-green-500' :
                          activity.type === 'card' ? 'bg-blue-500' : 'bg-basketball-orange-500'
                        }`}></div>
                        <span>{activity.description}</span>
                      </div>
                    ))
                  ) : (
                    <>
                  <div className="flex items-center space-x-3 text-sm">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span>Payment confirmed - 50,000 TZS</span>
                  </div>
                  <div className="flex items-center space-x-3 text-sm">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span>Digital card generated</span>
                  </div>
                  <div className="flex items-center space-x-3 text-sm">
                    <div className="w-2 h-2 bg-basketball-orange-500 rounded-full"></div>
                    <span>Profile updated</span>
                  </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
